import { parse, TYPE } from '@formatjs/icu-messageformat-parser';

const collectMessageContract = elements => {
  return elements.reduce((signatures, element) => {
    const hasArgument = [
      TYPE.argument,
      TYPE.number,
      TYPE.date,
      TYPE.time,
      TYPE.select,
      TYPE.plural,
    ].includes(element.type);

    if (hasArgument) {
      signatures.add(`argument:${element.type}:${element.value}`);
    }

    if (element.type === TYPE.select) {
      signatures.add(
        `select:${element.value}:${Object.keys(element.options)
          .sort()
          .join('|')}`
      );
    }

    if (element.type === TYPE.plural) {
      signatures.add(
        `plural:${element.value}:${element.pluralType || 'cardinal'}:${element.offset ||
          0}:${Object.keys(element.options)
          .sort()
          .join('|')}`
      );
    }

    if (element.type === TYPE.tag) {
      signatures.add(`tag:${element.value}`);
    }

    if (element.options) {
      Object.values(element.options).forEach(option => {
        collectMessageContract(option.value).forEach(signature => signatures.add(signature));
      });
    }

    if (element.type === TYPE.tag) {
      collectMessageContract(element.children).forEach(signature => signatures.add(signature));
    }

    return signatures;
  }, new Set());
};

const messageContract = message => [...collectMessageContract(parse(message))].sort();

const hasSameMessageContract = (sourceMessage, translatedMessage) => {
  const sourceSignatures = messageContract(sourceMessage);
  const translatedSignatures = messageContract(translatedMessage);

  return (
    sourceSignatures.length === translatedSignatures.length &&
    sourceSignatures.every((signature, index) => signature === translatedSignatures[index])
  );
};

export const isCompatibleTranslation = (sourceMessage, translatedMessage) => {
  if (typeof sourceMessage !== 'string' || typeof translatedMessage !== 'string') {
    return false;
  }

  try {
    return hasSameMessageContract(sourceMessage, translatedMessage);
  } catch (error) {
    return false;
  }
};

export const mergeValidTranslations = (sourceTranslations, targetTranslations) => {
  return Object.entries(sourceTranslations).reduce((translations, [key, sourceMessage]) => {
    const translatedMessage = targetTranslations[key];

    translations[key] = isCompatibleTranslation(sourceMessage, translatedMessage)
      ? translatedMessage
      : sourceMessage;
    return translations;
  }, {});
};

export const mergeTranslationsWithLocalOverrides = (
  localTranslations,
  hostedTranslations,
  localOverrideKeys
) => {
  const mergedTranslations = { ...localTranslations, ...hostedTranslations };

  return localOverrideKeys.reduce((translations, key) => {
    if (Object.prototype.hasOwnProperty.call(localTranslations, key)) {
      translations[key] = localTranslations[key];
    }
    return translations;
  }, mergedTranslations);
};
